import { createTheme, Grid, Input, Slider, ThemeProvider } from '@mui/material';
import { useStyles } from '@innovyze/stylovyze';
import * as React from 'react';

const getTrackColor = (
	invalid: boolean,
	disabled: boolean | undefined,
	accent: string | undefined,
	tone: 'primary' | 'neutral',
	primaryColor: string,
): string => {
	if (invalid) return '#d64545';
	if (disabled) return '#c7ccd4';
	if (accent) return accent;
	if (tone === 'neutral') return '#7c879a';
	return primaryColor;
};

const getRailHeight = (
	redesigned: boolean,
	tone: 'primary' | 'neutral',
	invalid: boolean,
): number => {
	if (!redesigned) return 0;
	return tone === 'primary' && !invalid ? 6 : 5;
};

const buildRedesignedSliderThemeOptions = (
	trackColor: string,
	railHeight: number,
	disabled: boolean | undefined,
) => ({
	components: {
		MuiSlider: {
			styleOverrides: {
				root: { color: trackColor, height: 4, padding: '14px 0' },
				rail: {
					height: railHeight,
					borderRadius: railHeight,
					backgroundColor: '#e6eaf0',
					opacity: 1,
				},
				track: {
					height: railHeight,
					borderRadius: railHeight,
					border: 'none',
				},
				thumb: {
					height: 18,
					width: 18,
					backgroundColor: '#ffffff',
					border: `1px solid ${disabled ? '#d5dbe3' : trackColor}`,
					boxShadow: '0 1px 4px rgba(16,24,40,0.25)',
					'&:hover, &.Mui-focusVisible': {
						boxShadow: '0 2px 8px rgba(16,24,40,0.3)',
					},
					'&.Mui-active': {
						boxShadow: '0 2px 10px rgba(16,24,40,0.35)',
					},
				},
				mark: {
					backgroundColor: 'transparent',
				},
				markLabel: {
					fontSize: 11,
					color: '#97a0ac',
					top: 26,
					fontFamily: 'inherit',
				},
				valueLabel: {
					top: 22,
					backgroundColor: 'transparent',
					color: '#5b6472',
					fontSize: 11,
					fontFamily: 'inherit',
				},
			},
		},
	},
});

// Pre-computed once so the classic path's useMemo returns instantly with no
// createTheme call per render.
const NOOP_THEME = createTheme();

export interface SliderProps {
	invalid?: boolean;
	value: number;
	dataCy?: string;
	onSliderChanged?: (value: number) => void;
	color?: 'primary' | 'secondary';
	disabled?: boolean;
	marks?: 'off' | 'minmax' | 'minmaxticks' | '25' | '50';
	ThumbComponent?: React.ElementType<React.HTMLAttributes<HTMLSpanElement>>;
	track?: 'normal' | false | 'inverted';
	// ValueLabelComponent?: React.ElementType<ValueLabelProps>;
	valueLabelDisplay?: 'on' | 'auto' | 'off';
	padLeft?: string;
	/** Opt into the redesigned risk look (thin rail, tonal fill, soft handle).
	 *  Defaults to false so every existing usage renders exactly as before. */
	redesigned?: boolean;
	/** 'primary' = the accent (category, the big lever); 'neutral' = grey
	 *  (component, local within a category). Only used when `redesigned`. */
	tone?: 'primary' | 'neutral';
	/** Explicit fill/handle colour — overrides `tone`. Only used when `redesigned`. */
	accent?: string;
	/** Cap the slider+input width so it doesn't stretch across a wide card.
	 *  Only used when `redesigned`. */
	maxWidth?: number | string;
	/** Drag increment — snap to rounder values. Only used when `redesigned`
	 *  (the classic path is always 1). */
	step?: number;
}

export const PercentSlider = ({
	invalid = false,
	dataCy,
	onSliderChanged,
	disabled,
	marks,
	value,
	valueLabelDisplay,
	padLeft,
	redesigned = false,
	tone = 'primary',
	accent,
	maxWidth,
	step = 1,
}: SliderProps) => {
	const { themePalette: palette } = useStyles();
	// This needs to be done this way otherwise, for some reason, the slider stops sliding !
	const CustomSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: palette.primary.main,
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: palette.primary.main,
						color: palette.primary.main,
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						top: '23px',
						backgroundColor: 'transparent',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 2,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const InvalidSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: palette.error.dark,
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: palette.error.dark,
						color: palette.error.dark,
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						backgroundColor: 'transparent',
						top: '23px',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 1,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const DisabledSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: '#bdbdbd',
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: '#bdbdbd',
						color: '#bdbdbd',
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						backgroundColor: 'transparent',
						top: '23px',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 2,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const getMarks = (
		marks?: 'off' | 'minmax' | 'minmaxticks' | '25' | '50',
	) => {
		switch (marks) {
			case 'off': {
				return;
			}
			case 'minmax': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case 'minmaxticks': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 25,
					},
					{
						value: 50,
					},
					{
						value: 75,
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case '25': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 25,
						label: '25%',
					},
					{
						value: 50,
						label: '50%',
					},
					{
						value: 75,
						label: '75%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case '50': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 50,
						label: '50%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
		}
	};

	const handleSliderChange = (_event: Event, newValue: number | number[]) => {
		if (typeof newValue === 'number') {
			onSliderChanged?.(newValue);
		}
	};

	const handleSliderChangeCommitted = (
		_event: React.SyntheticEvent | Event,
		newValue: number | number[],
	) => {
		if (typeof newValue === 'number') {
			value = newValue;
			onSliderChanged?.(newValue);
		}
	};

	const handleBlur = () => {
		if (value < 0) {
			onSliderChanged?.(0);
		} else if (value > 100) {
			onSliderChanged?.(100);
		}
	};

	const getTheme = () => {
		if (disabled) return DisabledSlider;
		else if (invalid) return InvalidSlider;
		else return CustomSlider;
	};

	// A calm, modern slider: thin light rail, coloured fill, a soft white
	// handle. Built as a theme (rather than sx) because overriding via sx alone
	// intermittently breaks dragging on this MUI version.
	// These are only needed on the redesigned path — keep them cheap when classic.
	const trackColor = redesigned
		? getTrackColor(invalid, disabled, accent, tone, palette.primary.main)
		: '';
	const railHeight = getRailHeight(redesigned, tone, invalid);

	// useMemo must be unconditional (Rules of Hooks), but we short-circuit to a
	// pre-built no-op theme when redesigned=false so classic renders stay free
	// of the createTheme overhead.
	const redesignedTheme = React.useMemo(() => {
		if (!redesigned) return NOOP_THEME;
		return createTheme(
			buildRedesignedSliderThemeOptions(trackColor, railHeight, disabled),
		);
	}, [redesigned, trackColor, railHeight, disabled]);

	if (redesigned) {
		return (
			<Grid
				container
				alignItems="center"
				alignContent="center"
				wrap="nowrap"
				sx={{ width: '100%', maxWidth }}>
				<Grid
					item
					xs
					style={{
						paddingLeft: padLeft || 0,
						paddingRight: '16px',
					}}>
					<ThemeProvider theme={redesignedTheme}>
						<Slider
							data-cy={dataCy}
							valueLabelDisplay={valueLabelDisplay}
							value={value}
							disabled={disabled}
							onChange={handleSliderChange}
							onChangeCommitted={handleSliderChangeCommitted}
							min={0}
							max={100}
							marks={getMarks(marks)}
							step={step}
						/>
					</ThemeProvider>
				</Grid>
				<Grid item>
					<Input
						disableUnderline
						value={value}
						disabled={disabled}
						onChange={e => {
							onSliderChanged?.(
								e.target.value === ''
									? 0
									: Number(e.target.value),
							);
						}}
						onBlur={handleBlur}
						sx={{
							width: 78,
							border: '1px solid #d5dbe3',
							borderRadius: '8px',
							px: 1.25,
							py: 0.7,
							fontSize: 14,
							fontFamily: 'inherit',
							color: '#1b2733',
							background: disabled ? '#f5f7fa' : '#fff',
							'&.Mui-focused': {
								borderColor: palette.primary.main,
							},
							'& input': {
								textAlign: 'right',
								padding: 0,
								MozAppearance: 'textfield',
							},
							'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
								{
									WebkitAppearance: 'none',
									margin: 0,
								},
						}}
						inputProps={{
							step,
							min: 0,
							max: 100,
							type: 'number',
							'aria-labelledby': 'input-slider',
						}}
						endAdornment={
							<span
								style={{
									color: '#97a0ac',
									fontSize: 14,
									paddingLeft: 3,
								}}>
								%
							</span>
						}
					/>
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container alignItems="center" alignContent="center" wrap="nowrap">
			<Grid
				item
				xs={11}
				style={{
					paddingLeft: padLeft ? padLeft : 0,
					paddingRight: '30px',
				}}>
				<ThemeProvider theme={getTheme()}>
					<Slider
						data-cy={dataCy}
						valueLabelDisplay={valueLabelDisplay}
						value={value}
						disabled={disabled}
						onChange={handleSliderChange}
						onChangeCommitted={handleSliderChangeCommitted}
						min={0}
						max={100}
						marks={getMarks(marks)}
						step={1}
					/>
				</ThemeProvider>
			</Grid>
			<Grid item xs={1} style={{ minWidth: '85px' }}>
				<Input
					style={{ width: '85px' }}
					value={value}
					disabled={disabled}
					margin="dense"
					onChange={e => {
						onSliderChanged?.(
							e.target.value === '' ? 0 : Number(e.target.value),
						);
					}}
					onBlur={handleBlur}
					inputProps={{
						step: 1,
						min: 0,
						max: 100,
						type: 'number',
						'aria-labelledby': 'input-slider',
					}}
					endAdornment={'%'}
				/>
			</Grid>
		</Grid>
	);
};
